import React, { useEffect, useState } from 'react';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import appSettings from '../../../config/settings';
import { pathByRouteName } from '../../../util/routes';

import {
  LimitedAccessBanner,
  StripeVerificationBanner,
  Modal,
  ModalMissingInformation,
} from '../../../components';

import TopbarNotice from './TopbarNotice/TopbarNotice';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import TopbarMobile from './TopbarMobile/TopbarMobile';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';

import css from './Topbar.module.css';

const MOBILE_MENU_MODAL_ID = 'TopbarMobileMenu';
// On <html> while the phone menu is open; see marketplaceDefaults.css.
const MOBILE_MENU_OPEN_CLASS = 'mobileMenuOpen';

/**
 * Top-level topbar renderer. Composes:
 *
 * - TopbarNotice – black announcement strip above everything.
 * - LimitedAccessBanner – preserved from upstream; surfaces "logged in as
 *   another user" warnings.
 * - TopbarDesktop / TopbarMobile – chosen at the CSS layer so SSR renders
 *   both and the layout swap happens via media queries.
 * - TopbarMobileMenu – rendered inside a Modal; opens when the mobile
 *   hamburger is tapped.
 * - ModalMissingInformation – preserved from upstream; nudges users with
 *   incomplete profiles, unverified email, etc.
 *
 * @param {Object} props – the full set of props provided by TopbarContainer
 * @returns {JSX.Element}
 */
const Topbar = props => {
  const {
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    showStripeVerificationNotice,
    history,
    onLogout,
    // When true, render a stripped-down topbar suitable for full-screen
    // flows like the listing wizard: no TopbarNotice strip, no limited-
    // access banner, no category nav, and the brand logo aligned to the
    // left edge instead of being absolutely centered. The desktop variant
    // still keeps the right-side icon row (search / heart / cart /
    // profile) so authenticated chrome remains available.
    compact = false,
  } = props;

  const routeConfiguration = useRouteConfiguration();

  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  // The Konvers chat bubble is injected by a third-party script outside the
  // React tree, so the only clean handle on it is a class on the document.
  // marketplaceDefaults.css hides the bubble while this is set: with the menu
  // open it sat on top of the bottom links.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    document.documentElement.classList.toggle(MOBILE_MENU_OPEN_CLASS, isMobileMenuOpen);
    return () => document.documentElement.classList.remove(MOBILE_MENU_OPEN_CLASS);
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging.
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }
    });
  };

  return (
    <div className={css.root}>
      {compact ? null : <TopbarNotice />}

      {compact ? null : (
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          isLoggedInAs={isLoggedInAs}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={handleLogout}
          currentPage={currentPage}
        />
      )}

      {compact ? null : (
        <StripeVerificationBanner show={showStripeVerificationNotice} currentPage={currentPage} />
      )}

      <div className={css.desktopOnly}>
        <TopbarDesktop
          isAuthenticated={isAuthenticated}
          currentUser={currentUser}
          onLogout={handleLogout}
          compact={compact}
        />
      </div>

      <div className={css.mobileOnly}>
        <TopbarMobile
          isAuthenticated={isAuthenticated}
          onMenuOpen={() => setMobileMenuOpen(true)}
        />
      </div>

      <Modal
        id={MOBILE_MENU_MODAL_ID}
        scrollLayerClassName={css.mobileMenuOverlay}
        containerClassName={css.mobileMenuPanel}
        contentClassName={css.mobileMenuContent}
        closeNavClassName={css.mobileMenuCloseNav}
        closeButtonRootClassName={css.mobileMenuClose}
        closeOnOverlayClick
        isOpen={isMobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <TopbarMobileMenu
          isAuthenticated={isAuthenticated}
          isOpen={isMobileMenuOpen}
          currentUser={currentUser}
          onClose={() => setMobileMenuOpen(false)}
          onLogout={handleLogout}
          currentPage={currentPage}
        />
      </Modal>

      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={history?.location}
        notificationCount={notificationCount}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
        showGenericError={showGenericError}
      />
    </div>
  );
};

export default Topbar;
